import {
  getLeoUuid,
  LeoUUID,
  getBoolean,
  getList,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { SpocUserDetails, SpocUserDetailsDTO } from "../types/spocUserDetails";
/* eslint-disable import/export */
export abstract class GetDashboardViewAndProjectSpocsRPC implements
  LeoRPC<GetDashboardViewAndProjectSpocsRPC.Request, GetDashboardViewAndProjectSpocsRPC.Response, GetDashboardViewAndProjectSpocsRPC.Errors.Errors>
{
  abstract execute(
    request: GetDashboardViewAndProjectSpocsRPC.Request
  ): Promise<LeoRPCResult<GetDashboardViewAndProjectSpocsRPC.Response, GetDashboardViewAndProjectSpocsRPC.Errors.Errors>>;
}

export namespace GetDashboardViewAndProjectSpocsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    surveyId: string;
    surveyViewId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetDashboardViewAndProjectSpocsRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly surveyId: LeoUUID;
    readonly surveyViewId: LeoUUID;
  
  
    constructor(
    projectId: LeoUUID, 
    surveyId: LeoUUID, 
    surveyViewId: LeoUUID
    ) {
      this.projectId = projectId;
      this.surveyId = surveyId;
      this.surveyViewId = surveyViewId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const surveyViewId = getLeoUuid(dto, "surveyViewId", InvalidRequestSurveyViewIdError);
      return new Request(
        projectId,
        surveyId,
        surveyViewId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          surveyId: this.surveyId.toDTO(),
          surveyViewId: this.surveyViewId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `GetDashboardViewAndProjectSpocsRPC(projectId='${this.projectId}',surveyId='${this.surveyId}',surveyViewId='${this.surveyViewId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    isAccessibleByProjectSPoCs: boolean;
    projectSPoCUserDetails: SpocUserDetailsDTO[];
    selectedSPoCUserDetails: SpocUserDetailsDTO[];
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetDashboardViewAndProjectSpocsResponse: boolean = true;
  
    readonly isAccessibleByProjectSPoCs: boolean;
    readonly projectSPoCUserDetails: SpocUserDetails[];
    readonly selectedSPoCUserDetails: SpocUserDetails[];
  
  
    constructor(
    isAccessibleByProjectSPoCs: boolean, 
    projectSPoCUserDetails: SpocUserDetails[], 
    selectedSPoCUserDetails: SpocUserDetails[]
    ) {
      this.isAccessibleByProjectSPoCs = isAccessibleByProjectSPoCs;
      this.projectSPoCUserDetails = projectSPoCUserDetails;
      this.selectedSPoCUserDetails = selectedSPoCUserDetails;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const isAccessibleByProjectSPoCs = getBoolean(dto, "isAccessibleByProjectSPoCs", InvalidResponseIsAccessibleByProjectSPoCsError);
      const projectSPoCUserDetails = getList<SpocUserDetailsDTO, SpocUserDetails>(
        dto,
        "projectSPoCUserDetails",
        SpocUserDetails.fromDTO,
        InvalidResponseProjectSPoCUserDetailsError
      );
      const selectedSPoCUserDetails = getList<SpocUserDetailsDTO, SpocUserDetails>(
        dto,
        "selectedSPoCUserDetails",
        SpocUserDetails.fromDTO,
        InvalidResponseSelectedSPoCUserDetailsError
      );
      return new Response(
        isAccessibleByProjectSPoCs,
        projectSPoCUserDetails,
        selectedSPoCUserDetails
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          isAccessibleByProjectSPoCs: this.isAccessibleByProjectSPoCs,
          projectSPoCUserDetails: this.projectSPoCUserDetails.map((e: SpocUserDetails) => { return e.toDTO() }),
          selectedSPoCUserDetails: this.selectedSPoCUserDetails.map((e: SpocUserDetails) => { return e.toDTO() })
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetDashboardViewAndProjectSpocs(isAccessibleByProjectSPoCs='${this.isAccessibleByProjectSPoCs}',projectSPoCUserDetails='${this.projectSPoCUserDetails}',selectedSPoCUserDetails='${this.selectedSPoCUserDetails}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    InvalidSurveyId = "INVALID_SURVEY_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    InvalidSurveyViewId = "INVALID_SURVEY_VIEW_ID"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `GetDashboardViewAndProjectSpocsRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `GetDashboardViewAndProjectSpocsRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `GetDashboardViewAndProjectSpocsRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface InvalidSurveyViewIdDTO {
      code: string;
    }
    
    export class InvalidSurveyViewId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyViewId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyViewId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyViewIdDTO {
        const dto: InvalidSurveyViewIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyViewId.prototype.toString = function toString(): string {
      return `GetDashboardViewAndProjectSpocsRPC.INVALID_SURVEY_VIEW_ID()`;
    };
    

    export type Errors = InvalidProjectId | InvalidSurveyId | ProjectAlreadyArchived | InvalidSurveyViewId;
    export type ErrorsDTO = InvalidProjectIdDTO | InvalidSurveyIdDTO | ProjectAlreadyArchivedDTO | InvalidSurveyViewIdDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.InvalidSurveyViewId:
          return new InvalidSurveyViewId();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends GetDashboardViewAndProjectSpocsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestSurveyIdError extends GetDashboardViewAndProjectSpocsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestSurveyViewIdError extends GetDashboardViewAndProjectSpocsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyViewIdError";
  }
}

export class InvalidResponseIsAccessibleByProjectSPoCsError extends GetDashboardViewAndProjectSpocsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseIsAccessibleByProjectSPoCsError";
  }
}

export class InvalidResponseProjectSPoCUserDetailsError extends GetDashboardViewAndProjectSpocsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectSPoCUserDetailsError";
  }
}

export class InvalidResponseSelectedSPoCUserDetailsError extends GetDashboardViewAndProjectSpocsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSelectedSPoCUserDetailsError";
  }
}

export class InvalidResponseInvalidProjectIdError extends GetDashboardViewAndProjectSpocsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends GetDashboardViewAndProjectSpocsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends GetDashboardViewAndProjectSpocsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseInvalidSurveyViewIdError extends GetDashboardViewAndProjectSpocsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyViewIdError";
  }
}

